import { Trans, useLingui } from '@lingui/react/macro';
import { Lock } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { ChampionshipMediaTab } from 'modules/championship/domain/enums/championship-media-tab';
import { useChampionshipMediaSettingsQuery } from 'modules/championship/store/championship-media-api';
import ChampionshipMediaResults from 'modules/championship/ui/components/form-parts/media/championship-media-results';
import ChampionshipMediaTemplates from 'modules/championship/ui/components/form-parts/media/championship-media-templates';
import ChampionshipMediaTemplatesSkeleton from 'modules/championship/ui/components/form-parts/media/championship-media-templates-skeleton';
import MediaTabStateCard from 'modules/championship/ui/components/form-parts/media/media-tab-state-card';
import { useChampionshipMediaJobs } from 'modules/championship/ui/hooks/use-championship-media-jobs';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useMemo } from 'react';
import { Navigate, Route, Routes, useMatch, useNavigate } from 'react-router-dom';
import { useOrganizerFeature } from 'shared/hooks/use-organizer-feature';
import { useAppSelector } from 'store/hooks';
import UDButton from 'ud-ui/components/button';
import { TabMenu } from 'ud-ui/components/tab-menu';

interface ChampionshipMediaPageProps {
  championshipId: number;
  /**
   * Организация, которой принадлежит соревнование (`championship.tournamentId` бэкенда).
   * Наборы берутся по ней, а не по активной организации в шапке: супер-админ открывает
   * турниры чужих организаций, а набор другой организации бэкенд привязать не даст (IMGM-1489).
   */
  organizerId: number;
}

const TAB_ORDER = [ChampionshipMediaTab.IMAGES, ChampionshipMediaTab.TEMPLATES];

/**
 * Вкладка «Медиа» соревнования: подвкладки «Картинки» и «Шаблоны», у каждой свой адрес.
 *
 * «Картинки» вынесены наверх отдельной подвкладкой со счётчиком черновиков, а не блоком под
 * списком типов: внизу длинной страницы их не находили (IMGM-1488). Счётчик и ссылка
 * «Посмотреть» в тосте генерации — то, что не даёт подвкладке спрятать результат.
 */
const ChampionshipMediaPage = ({ championshipId, organizerId }: ChampionshipMediaPageProps) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  // Тариф организации турнира, а не активной в шапке, и без поблажки супер-админу: генерацию бэкенд
  // проверяет по тарифу организации турнира и откажет любому (IMGM-1520).
  const { isEnabled, isFetching: isFeatureFetching } = useOrganizerFeature(organizerId, 'MEDIA_MANAGER');
  const activeOrganizerId = useAppSelector(authStoreUserTournamentId);
  /** «Выбрать тариф» ведёт на подписку активной организации — чужой организации она купила бы не тот тариф. */
  const isOwnOrganizer = activeOrganizerId === organizerId;
  const { data: settings, isFetching } = useChampionshipMediaSettingsQuery(championshipId, { skip: !isEnabled });
  const { data: jobs } = useChampionshipMediaJobs(championshipId, isEnabled ? organizerId : 0);
  const isTemplatesRoute = Boolean(useMatch(Pages.CHAMPIONSHIPS.MEDIA.TEMPLATES));

  const draftsCount = useMemo(() => jobs?.items.filter((job) => job.status === 'READY').length ?? 0, [jobs]);

  const tabs = useMemo(
    () => [
      { name: draftsCount > 0 ? t`Картинки · ${draftsCount}` : t`Картинки`, active: true },
      { name: t`Шаблоны`, active: true },
    ],
    [draftsCount, t],
  );

  const handleChangeTab = useCallback(
    (index: number) => navigate(Pages.CHAMPIONSHIPS.MEDIA.builders.tab(championshipId, TAB_ORDER[index]), { replace: true }),
    [navigate, championshipId],
  );

  if (isFeatureFetching) {
    return null;
  }

  if (!isEnabled && !isOwnOrganizer) {
    // Турнир чужой организации (супер-админ): тариф подключает организатор, кнопки покупки нет.
    return (
      <div className="w-full px-6 pb-10">
        <MediaTabStateCard
          data-testid="championship-media-no-plan"
          icon={<Lock size={24} />}
          title={t`У организации нет тарифа с медиа-менеджером`}
          subtitle={t`Генерация картинок станет доступна, когда организатор подключит тариф с медиа-менеджером.`}
        />
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className="w-full px-6 pb-10">
        <MediaTabStateCard
          data-testid="championship-media-upsell"
          icon={<Lock size={24} />}
          title={t`Медиа-менеджер недоступен в вашей подписке`}
          subtitle={t`Медиа-менеджер готовит картинки расписаний, результатов и таблиц турнира по вашим шаблонам. Подключите тариф с медиа-менеджером, чтобы пользоваться им.`}
          action={
            // Кнопка с `navigate`, а не `<Link><UDButton>`: интерактив в интерактиве давал два
            // фокуса с клавиатуры (IMGM-1510).
            <UDButton variant="primary" type="button" onClick={() => navigate(Pages.SETTINGS.builders.subscription('MEDIA_MANAGER'))}>
              <Trans>Выбрать тариф</Trans>
            </UDButton>
          }
        />
      </div>
    );
  }

  // Пока набора нет, смотреть нечего — первым делом его надо привязать на «Шаблонах».
  const defaultTab = settings && !settings.templateGroup ? ChampionshipMediaTab.TEMPLATES : ChampionshipMediaTab.IMAGES;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col pb-6" data-testid="championship-media-page">
      <div className="mb-5">
        <TabMenu tabs={tabs} activeTab={isTemplatesRoute ? 1 : 0} onChange={handleChangeTab} />
      </div>
      {/* `flex-1` по всей цепочке: без высоты `UDEntityList` не центрирует пустое состояние «Картинок». */}
      <div className="flex min-h-0 w-full flex-1 flex-col px-6">
        <Routes>
          <Route
            path={ChampionshipMediaTab.IMAGES}
            element={<ChampionshipMediaResults championshipId={championshipId} organizerId={organizerId} />}
          />
          <Route
            path={ChampionshipMediaTab.TEMPLATES}
            element={
              <ChampionshipMediaTemplates
                championshipId={championshipId}
                organizerId={organizerId}
                settings={settings}
                isFetching={isFetching}
              />
            }
          />
          <Route
            path="*"
            element={
              // Та же заглушка, что у «Шаблонов» с набором: до ответа неизвестно, куда уведёт редирект,
              // а серый блок на всю ширину не походил ни на одну из подвкладок (IMGM-1491).
              isFetching && !settings ? (
                <ChampionshipMediaTemplatesSkeleton variant="assigned" />
              ) : (
                <Navigate to={Pages.CHAMPIONSHIPS.MEDIA.builders.tab(championshipId, defaultTab)} replace />
              )
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default ChampionshipMediaPage;
